import React from 'react';
import ChannelIcon, { EChannelIcon } from 'components/ChannelIcon';
import { PATH } from 'constants/path';

export enum EChannel {
    MESSAGE = 'message',
    SMS = 'sms',
    EMAIL = 'email',
    AVS = 'avs',
}

export type TGuideProvider = {
    name: string;
    link: string;
};

type TChannelConfig = {
    key: EChannel;
    label: string;
    // where the provider is supposed to paste it
    where: string;
    // optional deep link into the product, null when the channel lives outside Klarity
    cta?: string;
    href?: string;
    icon: React.ReactNode;
    text: (p: TGuideProvider) => string;
};

// The merge token providers are expected to replace before sending.
export const PATIENT_NAME_TOKEN = '{{Patient first name}}';

// Ready-to-send copy per channel, so nobody has to write it themselves.
export const CHANNELS: TChannelConfig[] = [
    {
        key: EChannel.MESSAGE,
        label: 'In-app message',
        where: 'Messages → patient thread',
        cta: 'Open Messages',
        href: `${PATH.DASHBOARD}/${PATH.INBOX}`,
        icon: <ChannelIcon type={EChannelIcon.CHAT} />,
        text: (p) =>
            `Hi ${PATIENT_NAME_TOKEN} — thanks for coming in today. If you have 30 seconds, would you mind sharing how your visit with ${p.name} went? It helps other patients find the right care.\n\n${p.link}`,
    },
    {
        key: EChannel.SMS,
        label: 'Text',
        where: 'Your phone or practice texting tool',
        icon: <ChannelIcon type={EChannelIcon.CHAT_ALT} />,
        text: (p) =>
            `${PATIENT_NAME_TOKEN}, thanks for visiting ${p.name} today! Mind leaving a quick review? It takes 30 seconds: ${p.link}`,
    },
    {
        key: EChannel.EMAIL,
        label: 'Email',
        where: 'Any email client — subject line included',
        icon: <ChannelIcon type={EChannelIcon.MAIL} />,
        text: (p) =>
            `Subject: How was your visit with ${p.name}?\n\nHi ${PATIENT_NAME_TOKEN},\n\nThank you for trusting us with your care. If you have a moment, we’d love to hear how your visit went — it only takes 30 seconds and helps other patients choose with confidence.\n\nLeave a review: ${p.link}\n\nThank you,\n${p.name}`,
    },
    {
        key: EChannel.AVS,
        label: 'After-visit summary',
        where: 'Added to the summary the patient receives',
        icon: <ChannelIcon type={EChannelIcon.DOC} />,
        text: (p) =>
            `We’d value your feedback on today’s visit with ${p.name}. Leave a quick review here (30 seconds): ${p.link}`,
    },
];

export const getChannel = (key: EChannel) => CHANNELS.find((c) => c.key === key) || CHANNELS[0];

// Copy that differs between a group practice admin sharing someone else's link
// and a solo provider sharing their own.
export const GUIDE_COPY = {
    group: {
        titleCopy: 'Copy their review link',
        descCopy: 'One permanent link per provider. It never expires, so it can be reused with every patient.',
        titleOutcome: 'The patient reviews them — and you keep control',
        outProfile:
            "Public reviews show on that provider's Klarity profile. Private feedback stays inside the practice.",
        outDispute: "The practice has <b>30 days</b> to reply or dispute anything before it's final.",
        liveCap: 'Public ones appear on the provider’s Klarity profile, and can be replied to for 30 days.',
    },
    solo: {
        titleCopy: 'Copy your review link',
        descCopy: 'One permanent link. It never expires, so you can reuse it with every patient.',
        titleOutcome: 'Your patient reviews you — and you keep control',
        outProfile: 'Public reviews show on your Klarity profile. Private feedback comes to you only.',
        outDispute: "You have <b>30 days</b> to reply or dispute anything before it's final.",
        liveCap: 'Public ones appear on your Klarity profile. You’re notified and can reply for 30 days.',
    },
};

export const PREVIEW_CAPS = [
    {
        t: '1. They get your message',
        d: 'Wherever you pasted the link — in-app message, text, email or after-visit summary.',
    },
    {
        t: '2. They sign in to the patient portal',
        d: 'Signing up with the phone or email on file is what makes their review a verified one.',
    },
    {
        t: '3. They rate the visit',
        d: 'Stars, quick tags, an optional comment, and whether it’s public or private.',
    },
    {
        t: '4. The review goes live',
        d: 'Public ones appear on the provider’s Klarity profile, and can be replied to for 30 days.',
    },
];
